interface CircularProgressProps {
  percentage: number;
  size?: number;
  strokeWidth?: number;
  color: string;
  children: React.ReactNode;
}

export default function CircularProgress({
  percentage,
  size = 120,
  strokeWidth = 8,
  color,
  children
}: CircularProgressProps) {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const isOverflow = percentage > 100;
  
  // Cap visual progress at 100%
  const displayPercentage = Math.min(percentage, 100);
  const offset = circumference - (displayPercentage / 100) * circumference;
  
  // Calculate overflow portion (for the second ring)
  const overflowPercentage = isOverflow ? Math.min(percentage - 100, 100) : 0;
  const overflowOffset = circumference - (overflowPercentage / 100) * circumference;

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg
        className="transform -rotate-90"
        width={size}
        height={size}
      >
        {/* Background circle */}
        <circle
          className="text-gray-200"
          strokeWidth={strokeWidth}
          stroke={isOverflow ? `${color}30` : "currentColor"}
          fill="transparent"
          r={radius}
          cx={size / 2}
          cy={size / 2}
        />
        
        {/* Main progress circle */}
        <circle
          className="transition-all duration-1000 ease-out"
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          stroke={color}
          fill="transparent"
          r={radius}
          cx={size / 2}
          cy={size / 2}
        />
        
        {/* Overflow circle - green ring showing excess */}
        {isOverflow && (
          <circle
            className="transition-all duration-1000 ease-out"
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={overflowOffset}
            strokeLinecap="round"
            stroke="#22c55e"
            fill="transparent"
            r={radius}
            cx={size / 2}
            cy={size / 2}
            style={{ filter: 'drop-shadow(0 0 4px rgba(34, 197, 94, 0.5))' }}
          />
        )}
      </svg>
      
      {/* Center content */}
      <div className="absolute inset-0 flex items-center justify-center">
        {children}
      </div>
      
      {/* Overflow badge */}
      {isOverflow && (
        <div className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center shadow-md">
          <span className="text-white text-[10px] font-bold">+</span>
        </div>
      )}
    </div>
  );
}